import { useIntl } from 'react-intl'
import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import styled from 'styled-components'
import useMeterWithReadings from './useMeterWithReadings'
import MeterDetailsTable from './MeterDetailsTable'
import type { ApartmentSelectOptions } from '@viilu-web/shared/types/apartment'
import MeterReadingTable from '../MeterReading/MeterReadingTable'
import InvoicesTable from 'ui/ManagerFinance/Invoices/InvoicesTable'
import LoadingState from 'ui/components/LoadingState'
import { LoadingContainer } from 'ui/ManagerFinance/components'

const NotFound = styled.p`
	margin: 16px;
`

const Content = styled.div`
	flex: 1;
	display: flex;
	overflow: auto;

	@media (max-width: 2050px) {
		flex-direction: column;
	}
`

const Left = styled.div`
	padding: 16px;
	border-right: 1px solid rgba(0, 0, 0, 0.2);
	height: 220px;

	@media (max-width: 2050px) {
		border-right: none;
		border-bottom: 1px solid rgba(0, 0, 0, 0.2);
		width: 100%;
	}
`

const Right = styled.div`
	padding: 16px;
	flex: 1;

	@media (max-width: 2050px) {
		width: 100%;
	}
`

const RightContent = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 400px;
	max-height: calc((100vh - 260px - 64px) / 2);
	overflow-y: auto;
	padding: 0 0 16px 0;
`

const Heading = styled.h2`
	margin: 0 0 16px 0;
`

type Props = {
	meterUUID: string
	onHideURL: string
	apartmentOptions: ApartmentSelectOptions[]
}

const MeterWithReadingsDialog = ({
	meterUUID,
	onHideURL,
	apartmentOptions,
}: Props) => {
	const intl = useIntl()
	const { data, refresh, loading } = useMeterWithReadings(meterUUID)

	const renderContent = () => {
		if (loading) {
			return (
				<LoadingContainer>
					<LoadingState />
				</LoadingContainer>
			)
		}
		if (!data) {
			return (
				<NotFound>
					{intl.formatMessage({
						defaultMessage: 'Mittaria ei löytynyt.',
						description: 'Message for meter not found.',
					})}
				</NotFound>
			)
		}

		return (
			<Content>
				<Left>
					<Heading>
						{intl.formatMessage({
							defaultMessage: 'Mittarintiedot',
							description: 'Heading for the meter readings table section.',
						})}
					</Heading>
					<MeterDetailsTable meter={data} apartmentOptions={apartmentOptions} />
				</Left>
				<Right>
					<RightContent>
						<Heading>
							{intl.formatMessage({
								defaultMessage: 'Mittausilmoitukset',
								description: 'Heading for meter reading section.',
							})}
						</Heading>
						<MeterReadingTable
							meterReadings={data?.readings || []}
							refresh={refresh}
							loading={loading}
							meter={data}
						/>
					</RightContent>
					<RightContent>
						<Heading>
							{intl.formatMessage({
								defaultMessage: 'Tasauslaskut',
								description: 'Heading for meter reading section.',
							})}
						</Heading>
						<InvoicesTable
							invoices={data?.invoices || []}
							refreshInvoices={refresh}
							apartmentOptions={apartmentOptions}
							loading={loading}
						/>
					</RightContent>
				</Right>
			</Content>
		)
	}

	const title = intl.formatMessage({
		defaultMessage: 'Mittaritiedot',
		description: 'Title for the meter with readings dialog: Meter data',
	})

	return (
		<ViiluFullPageDialog
			id="meter-with-readings-dialog"
			title={title}
			visible
			focusOnMount={false}
			toolbarStyle={{
				background: 'var(--color-secondary-dark)',
			}}
			onHideURL={onHideURL}
		>
			{renderContent()}
		</ViiluFullPageDialog>
	)
}

export default MeterWithReadingsDialog
